/**
 * Work out what a browser hostname means for tenant resolution.
 *
 *   platform  — our marketing/apex site, previews, localhost
 *   subdomain — <slug>.thepolishedversion.com (or <slug>.*.localhost in dev/tests)
 *   custom    — anything else: a tenant's own domain, looked up with
 *               get_org_by_domain (which matches both apex and www.)
 *
 * Only hosts under OUR root domain are treated as slugs. Previously any
 * three-label host (e.g. `acmecleaners.co.uk`) was mistaken for a subdomain.
 */
export const PLATFORM_ROOT_DOMAIN = 'thepolishedversion.com';

/** Labels that are never tenant slugs. */
export const RESERVED_SUBDOMAINS = new Set(['www', 'app', 'api', 'admin', 'mail', 'm']);

export type HostKind =
  | { kind: 'platform' }
  | { kind: 'subdomain'; slug: string }
  | { kind: 'custom'; host: string };

export function cleanHostname(hostname: string): string {
  let h = String(hostname ?? '').trim().toLowerCase();
  if (h.startsWith('[')) return h; // IPv6 literal
  h = h.split(':')[0];
  return h.replace(/\.+$/, '');
}

export function classifyHost(hostname: string): HostKind {
  const host = cleanHostname(hostname);

  if (
    !host ||
    host === 'localhost' ||
    host.startsWith('[') ||
    /^\d+\.\d+\.\d+\.\d+$/.test(host) ||
    host.endsWith('.vercel.app') ||
    host.endsWith('.supabase.co') ||
    host === PLATFORM_ROOT_DOMAIN
  ) {
    return { kind: 'platform' };
  }

  if (host.endsWith(`.${PLATFORM_ROOT_DOMAIN}`) || host.endsWith('.localhost')) {
    const slug = host.split('.')[0];
    return !slug || RESERVED_SUBDOMAINS.has(slug) ? { kind: 'platform' } : { kind: 'subdomain', slug };
  }

  // A single-label host (intranet name) can't be a public custom domain.
  if (!host.includes('.')) return { kind: 'platform' };

  return { kind: 'custom', host };
}

/** True on a tenant's own host (subdomain or custom domain), not our marketing apex/previews. */
export function isTenantHost(): boolean {
  return typeof window !== 'undefined' && classifyHost(window.location.hostname).kind !== 'platform';
}

/** The tenant's canonical origin in the browser: their live own domain, else the current host. */
export function tenantBrowserOrigin(customDomain: string | null | undefined): string {
  return customDomain ? `https://${customDomain.replace(/^www\./, '')}` : window.location.origin;
}
